/**
 * Automation Requests Invariants + selectors (Solicitudes tab — TV1-2377 slice 7)
 */

import { CommandOperationMap, Invariants, gateCommandByOperation } from '@commandDrivenStateEngine/core/invariants';
import { AutomationRequestsReadmodelType } from '@bett3r-dev/teselly-domain';
import { unfilledDeclaredInputs } from '@bett3r-dev/teselly-domain/automation/constants';
import { RequestsState, ShadowEvidence } from './state';
import { RequestsCommand } from './commands';

/** Sub-Goal statuses that are TERMINAL — mirrors the readmodel's own list. */
const TERMINAL_STATUSES = [ 'unsatisfiable', 'rejected', 'live', 'withdrawn' ];

/**
 * The Declared Inputs of the Sub-Goal's `approvedGraph` that have no value on its
 * bound rule, by input name — the SAME `unfilledDeclaredInputs` verdict the server
 * applies when refusing `RecordSubGoalPromoted` (TV1-2453).
 *
 * Values are read only from `observed` evidence (the Rules row's
 * `declaredInputsValues`). While evidence is `loading`, absent, or `no-rule`, there
 * is nothing to read, so every declared input counts as unfilled: we never offer a
 * click on a guess. A graph that declares no inputs yields `[]` regardless.
 */
export const unfilledInputsForPromotion = (
  subGoal: AutomationRequestsReadmodelType,
  evidence: ShadowEvidence | undefined
): string[] =>
  unfilledDeclaredInputs(
    subGoal.approvedGraph,
    evidence?.status === 'observed' ? evidence.declaredInputsValues : {}
  );

/**
 * THE PROMOTION GATE, in ONE place.
 *
 * `approved` — the same predicate the aggregate enforces with
 * `subGoalMustBeIn(['approved'])`. A `proposed`/`composed` Sub-Goal has not been
 * looked at by a human (D1: nothing executes before it has been); a `live` one is
 * already promoted; the terminal ones never will be.
 *
 * AND no Declared Input left empty (`unfilledInputsForPromotion`): the server
 * refuses promotion with `UNFILLED_DECLARED_INPUTS` otherwise, because an empty
 * input makes the rule live but silently inert. Offering the button in either
 * refused case produces a click the server rejects, which reads to the seller as a
 * broken app.
 */
export const canPromoteSubGoal = (
  subGoal: AutomationRequestsReadmodelType,
  evidence: ShadowEvidence | undefined
): boolean =>
  subGoal.status === 'approved' && unfilledInputsForPromotion( subGoal, evidence ).length === 0;

/** A request summary row for the list. Derived — never stored. */
export type RequestSummary = {
  requestId: string;
  submittedAt: string;
  subGoalCount: number;
  /** Zero open sub-goals === the request is done. An ABSENCE, hence derived. */
  isComplete: boolean;
  /** Count by status, for the list's status chips. */
  byStatus: Record<string, number>;
  /** The seller's own sentences, for the list's one-line preview. */
  specs: string[];
};

export const selectors = {
  /** Every Sub-Goal of the selected request, in submission order. */
  selectedSubGoals: ( state: RequestsState ): AutomationRequestsReadmodelType[] => {
    const { selectedRequestId } = state.ui;
    if ( !selectedRequestId ) return [];
    return state.data.subGoals.filter(( sg ) => sg.requestId === selectedRequestId );
  },

  /** Shadow evidence for a Sub-Goal, or `loading` while nothing has been read. */
  evidenceFor: ( state: RequestsState, subGoalId: string ): ShadowEvidence =>
    state.data.evidence[subGoalId] ?? { status: 'loading' },

  /** The list: one row per REQUEST, newest first. */
  requestSummaries: ( state: RequestsState ): RequestSummary[] => {
    const byRequest = new Map<string, RequestSummary>();

    for ( const sg of state.data.subGoals ) {
      const existing = byRequest.get( sg.requestId ) ?? {
        requestId: sg.requestId,
        submittedAt: sg.submittedAt,
        subGoalCount: 0,
        isComplete: true,
        byStatus: {},
        specs: []
      };
      existing.subGoalCount += 1;
      existing.byStatus[sg.status] = ( existing.byStatus[sg.status] || 0 ) + 1;
      if ( sg.spec ) existing.specs.push( sg.spec );
      if ( !TERMINAL_STATUSES.includes( sg.status )) existing.isComplete = false;
      if ( sg.submittedAt < existing.submittedAt ) existing.submittedAt = sg.submittedAt;
      byRequest.set( sg.requestId, existing );
    }

    return [...byRequest.values()].sort(( a, b ) => b.submittedAt.localeCompare( a.submittedAt ));
  }
};

/** Light — the views above are derived, so nothing needs clamping here. */
const normalize = ( state: RequestsState ): RequestsState => state;

/**
 * The user-sourced commands that reach the backend, each mapped to the ONE
 * AutomationRequests command it issues:
 *  - the promotion trigger → `RecordSubGoalPromoted`. `UnshadowRule` is issued by a
 *    server-side policy (D2); the UI must never send it, or the two-command
 *    handshake becomes a two-caller race.
 *  - the input-values trigger → `RecordSubGoalInputValues` (TV1-2453). The
 *    server-side assignation policy writes the values onto the rule; the UI never
 *    sends `SetRule` or `SetRuleDeclaredInputsValues` itself.
 */
export const commandOperations: CommandOperationMap<RequestsState, RequestsCommand> = {
  'automation/requests/ui/requestPromotion': 'RecordSubGoalPromoted',
  'automation/requests/ui/recordInputValues': 'RecordSubGoalInputValues'
};

const canApply = ({
  state,
  command,
  meta
}: {
  state: RequestsState;
  command: RequestsCommand;
  meta: import( '@commandDrivenStateEngine/core/types' ).CommandMeta;
}): boolean => {
  const { source } = meta;
  const { type } = command;

  if ( source === 'api' ) {
    return (
      type.startsWith( 'automation/requests/data/' ) ||
      type.startsWith( 'automation/requests/loading/' ) ||
      type === 'automation/requests/ui/clearPendingPromotion' ||
      type === 'automation/requests/ui/clearPendingInputValues'
    );
  }

  if ( source === 'user' ) {
    if ( !type.startsWith( 'automation/requests/ui/' )) return false;
    return gateCommandByOperation( commandOperations, { state, command, meta });
  }

  if ( source === 'policy' || source === 'application' ) return true;

  return false;
};

const validate = ( _state: RequestsState ): Record<string, string> | null => null;

export const invariants: Invariants<RequestsState, RequestsCommand> = {
  normalize,
  canApply,
  validate
};
